import { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';

// ── STATIC IMPORTS (initial bundle - core only) ──
import Layout, { navigation } from "./Layout.jsx";
import Login from "./Login.jsx";
import Register from "./Register.jsx";
import Confirm from "./Confirm.jsx";
import ForgotPassword from "./ForgotPassword.jsx";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { useAuth } from "@/lib/AuthContext";
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { AppProvider } from '@/components/hooks/useApp';
import OnboardingGate from '@/components/onboarding/OnboardingGate';
import FirstCompanyGate from '@/components/onboarding/FirstCompanyGate';
import TermsGate from '@/components/auth/TermsGate';
import { PageLoader } from '@/pages/routeHelpers';
import { routes as platformRoutes } from '@/features/platform/routes';
import { routes as accountingRoutes } from '@/features/accounting/routes';
import { routes as billingRoutes } from '@/features/billing/routes';
import { routes as bankingRoutes } from '@/features/banking/routes';
import { routes as fiscalRoutes } from '@/features/fiscal/routes';
import { routes as hrRoutes } from '@/features/hr/routes';
import { routes as operationsRoutes } from '@/features/operations/routes';
import { routes as aiRoutes } from '@/features/ai/routes';






// ── LAZY IMPORTS BY DOMAIN (code-split - loaded on demand) ──

// Dashboard configurable
// DEV-only visual showroom for the ARIA Mission Control redesign (no auth/data).
const MissionControlPreview = lazyWithRetry(() => import("@/components/ai/mission-control/MissionControlPreview"));
const UxLab = lazyWithRetry(() => import("./UxLab"));
const CreateModalsHarness = lazyWithRetry(() => import("@/components/common/__harness__/CreateModalsHarness"));
const InventarioHarness = lazyWithRetry(() => import("@/components/inventory/__harness__/InventarioHarness"));
const EmpleadosHarness = lazyWithRetry(() => import("@/components/hr/__harness__/EmpleadosHarness"));

// Finanzas & Contabilidad
// PR14 reinvención Conta — la legacy `Contabilidad.jsx` (10 tabs) se retiró;
// `/contabilidad` redirige al hub `/accounting` (mapeo ?tab= → lente).
// Cartera — unidades de ingreso recurrente (motor revenue_units, Fase D).
// Ficha de un activo de cartera (drill-down + asociaciones, Fase E4).
// Accounting hub (FASE 1 PR 1.4) — coexiste con `Contabilidad`. Cuando
// el nuevo flujo esté validado, las pantallas duplicadas se retirarán
// del legacy en una sesión posterior.
// PR-AD-4a — dispatcher del index: Resumen modo asesor (god_mode) vs Overview clásico.
// agentic-erp#925 — libros IRPF por actividad + catálogo de actividades.
// Libros IRPF — lente que absorbe Actividades económicas (tab). /economic-activities redirige.
// Cuentas Anuales — lente única del depósito anual (Depósito / ECPN / Memoria /
// Inventarios / XBRL en tabs). /ecpn, /memoria, /inventory-books y /xbrl
// redirigen aquí con ?tab= (preservando modelo/ejercicio).


// Patrimonio y provisiones — lente única (Ampliaciones / Dividendos /
// Provisiones en tabs). Las 3 rutas antiguas redirigen aquí con ?tab=.
// Anticipos — lente única (Clientes / Proveedores en tabs). Las 2 rutas
// antiguas redirigen aquí con ?tab=.
// Calendario fiscal — lente que absorbe M-202 (tab). /tax-prepayments redirige.
// Inmovilizado — lente única (Intangible / Material / Leasing en tabs). Las 3
// rutas antiguas redirigen aquí con ?tab=.
// Centro de Datos — pipeline de migración. Vive FUERA de /accounting: es un
// módulo transversal (contactos, empleados, nóminas, leads), no una lente
// contable. `/accounting/import` queda como redirect para bookmarks.
// Fase 2 rediseño conta — la lente `autopilot` es ahora la torre de control de
// autonomía de ARIA (parte diario firmable + dial + feed cross-día con Deshacer).
// Rediseño 360 Capa 2 — lentes reales que reemplazan los stubs ComingSoonLens.

// RRHH & Nóminas
// FASE 5 Sub-PR 5.2 — Portal del Asesor (skeleton; contenido en 5.3+5.4).
// PR-AD-0 — Contabilidad · Modo Asesor (workspace por cliente, shell propio).
const AdvisorWorkspace = lazyWithRetry(() => import("@/components/advisor/AdvisorWorkspace"));
// Flujo de bienvenida — aterrizaje de una empresa recién creada, shell propio.
const Bienvenida = lazyWithRetry(() => import("./Bienvenida"));
// SILTRA A.3 — configuración del despacho (Nº Autorización RED de la asesoría).
// Sprint 5.bis.5 — Calendario fiscal cross-empresa.
// Sprint 5.bis.1.a — Bulk AEAT prepare page (FirmModelsPage).
// Sprint 5.bis.4 — Bulk AEAT Wow screen (presentación en curso) + Result screen (post-submit).
// Fase E G601 — wizard upload-response asesor (último paso post AEAT portal + Autofirma).
// Sprint 5.bis.2.a — Bulk doc reclamation (Pantalla 3).
// Modo Asesor v2 — Notificaciones / Honorarios / Informes cross-cartera.
// Sprint 5.bis.0.4 — config de asesoría por company (owner-only).
// Sprint 5.bis.0.5 — página pública /invitacion/:token (sin AuthGuard).
const Invitacion = lazyWithRetry(() => import("./Invitacion"));
// Modo Asesor v2 — aceptación de invitación firm→empresa.
const InvitacionEmpresa = lazyWithRetry(() => import("./InvitacionEmpresa"));

// CRM — hub único compacto (CRMPipeline). Actividades y Segmentación son ahora
// tabs del hub (?tab=), las páginas standalone redirigen aquí.
// Listado de Clientes/Proveedores retirado — el directorio unificado /contactos
// lo reemplaza. Las fichas 360 (ClienteDetalle/ProveedorDetalle) se conservan.

// Inventario

// Proyectos
const PublicProjectForm = lazyWithRetry(() => import("./PublicProjectForm"));
const PublicQuoteView = lazyWithRetry(() => import("./PublicQuoteView"));
const PublicInvoiceView = lazyWithRetry(() => import("./PublicInvoiceView"));
const ClientProjectView = lazyWithRetry(() => import("./ClientProjectView"));

// Analytics & BI — Analytics, BIDashboard and ExecutiveSummary are now
// mounted as tabs inside InsightsHub. Old routes redirect to /insights?tab=.

// AI & Agentic

// Configuración & Admin

// Legal & Privacy
const PrivacyPolicy = lazyWithRetry(() => import("./PrivacyPolicy"));

// Catch-all 404 — kept lazy so it doesn't bloat the initial bundle

// Compliance & Fiscal — VeriFactu and TicketBAIStatus are mounted as tabs
// inside ImpuestosHub. AEATHub is also a tab. Old routes redirect via the
// hub's ?tab= query param for bookmark stability.
// TicketBAIConfig fusionada dentro de ConfiguracionFiscal como el tab
// "ticketbai" (ver ConfiguracionFiscal.jsx). El path legacy /ticket-bai-config
// redirige a /configuracion-fiscal?tab=ticketbai.

// Docs

// ── PAGE REGISTRY (for URL matching - string array, no code weight) ──
const PAGE_NAMES = [
  // OJO: lo que NO esté aquí cae al fallback `'Home'` de `_getCurrentPage`, y eso tiene
  // dos efectos visibles: la miga de pan muestra el título de Inicio, y el sidebar marca
  // Inicio como activo mientras estás en otra página. Al añadir una entrada al menú de
  // `Layout.jsx` hay que añadirla TAMBIÉN aquí.
  //
  // Estas cinco eran de primer nivel y llevaban tiempo cayendo al fallback: Contactos,
  // Cartera, Contabilidad (`Accounting`), Mis Tareas y el Portal del Asesor.
  //
  // Si la URL NO deriva del pageName (`/datos` → `DataImport`), añadirla aquí no sirve
  // de nada: eso lo resuelve `PATH_TO_PAGE_NAME`, derivado del `path`/`matchPaths` que
  // declare la entrada en `navigation`.
  'Home','Dashboard','Analytics','ApprovalsDashboard',
  'Contactos','Cartera','Accounting','MyTasks','PortalAsesor',
  'AsistenteAgentic','BIDashboard',
  'BancoCuenta','Bancos','CRMActividades','CRMPipeline',
  'CRMSecuencias','CRMSegmentacion',
  'Clientes','Conciliacion','Configuracion',
  'ContratosRRHH','DietasGastos','EmployeeDetail',
  'ExecutiveSummary','FacturaDetalle','FacturaForm','QuoteForm',
  'Facturacion','Facturas','Finanzas','Gastos','GastosEscaneos','BandejaDocumentos','HRCommunications',
  'IESEPanel','Impuestos','Insights','Informes','InformesRRHH',
  'Inventario','InventarioMovimientos','InventarioPedidos','InventarioStock','InventarioRecuento',
  'Nominas','NominaReviewer','Onboarding','Organigrama','PlantillasFactura',
  'PortalEmpleado','Productos','ProjectDetail','Recursos',
  'Projects','ProjectBillingOverview','Proveedores',
  'RRHHEmpleados',
  'RentabilidadProyectos','RolesManagement','SalarySimulator','SkillsManagement',
  'SupervisorPanel','TicketBAIConfig','ConfiguracionFiscal','TicketBAIStatus','TimesheetManager','AIConversationsDashboard',
  'UserManual','VeriFactu','Soporte',
  'ProjectKanban',
  'ProyectoInversion',
];

// Rutas cuya URL NO deriva del pageName, así que buscar el último segmento en
// PAGE_NAMES no las encuentra por mucho que se añadan a la lista: `/datos` es
// `DataImport` y `/mis-empresas` es `PortalAsesor`. Se deriva del propio árbol de
// navegación (`path` y `matchPaths`) para no mantener una segunda lista a mano:
// lo que declara el menú es lo que resuelve la miga y el elemento activo.
const PATH_TO_PAGE_NAME = (() => {
  const map = {};
  const walk = (items) => {
    for (const item of items) {
      if (item.subItems) walk(item.subItems);
      if (!item.pageName) continue;
      for (const p of [item.path, ...(item.matchPaths || [])]) {
        if (p) map[p.toLowerCase()] = item.pageName;
      }
    }
  };
  walk(navigation);
  return map;
})();

export function _getCurrentPage(url) {
  if (url.endsWith('/')) url = url.slice(0, -1);
  const path = url.split('?')[0].toLowerCase();
  if (PATH_TO_PAGE_NAME[path]) return PATH_TO_PAGE_NAME[path];
  // Las lentes comparten el hub de Contabilidad: su último segmento no es un
  // pageName del menú global, pero deben conservar la miga y el item del módulo.
  if (path.startsWith('/accounting/')) return 'Accounting';
  let urlLastPart = url.split('/').pop();
  if (urlLastPart.includes('?')) urlLastPart = urlLastPart.split('?')[0];
  const normalized = urlLastPart.replace(/-/g, '').toLowerCase();
  const pageName = PAGE_NAMES.find(p => p.toLowerCase().replace(/-/g, '') === normalized);
  return pageName || 'Home';
}

export function AuthGuard({ children }) {
  const { isAuthenticated, isLoadingAuth } = useAuth();
  if (isLoadingAuth) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // La pantalla de términos va AQUÍ, dentro del guard, y no junto a
  // `FirstCompanyGate` en el catch-all: así cubre también `/bienvenida` y
  // `/asesor/:companyId`, que llevan su propio AuthGuard. Sin eso, una cuenta sin
  // empresa —justo la que entra por Google sin aceptación registrada— la esquivaba
  // porque `FirstCompanyGate` la manda a `/bienvenida`. Las rutas públicas
  // (`/login`, `/register`, `/confirm`, `/privacy`, portales por token) no pasan
  // por el guard y no se bloquean. No parpadea: cuando el guard deja pasar, el
  // `/me` de AuthContext ya ha resuelto y `terms_required` está en `user`.
  return <TermsGate>{children}</TermsGate>;
}

function PagesContent() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  // Strip trailing slash (except root "/") so direct URL access to e.g. /veri-factu/ works
  if (location.pathname !== '/' && location.pathname.endsWith('/')) {
    return <Navigate to={location.pathname.slice(0, -1) + location.search} replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/confirm" element={<Confirm />} />      {/* Ruta pública para vista de cliente - sin autenticación */}
      <Route path="/project-view/:id" element={
        <Suspense fallback={<PageLoader />}>
          <ClientProjectView />
        </Suspense>
      } />
      <Route path="/privacy" element={
        <Suspense fallback={<PageLoader />}>
          <PrivacyPolicy />
        </Suspense>
      } />
      <Route path="/project-form/:formId" element={
        <Suspense fallback={<PageLoader />}>
          <PublicProjectForm />
        </Suspense>
      } />
      {/* VEN-esign — vista pública de firma de presupuesto. Acceso anónimo
          (token = scope), FUERA del layout autenticado. */}
      <Route path="/quote-view/:token" element={
        <Suspense fallback={<PageLoader />}>
          <PublicQuoteView />
        </Suspense>
      } />
      {/* Portal público de factura — el destino del enlace del email
          `invoice_sent`, que hasta ahora daba 404 porque esta ruta no existía.
          Acceso anónimo (public_token = scope), FUERA del layout autenticado:
          dentro del catch-all lo envolvería AuthGuard y mandaría al receptor
          a /login. */}
      <Route path="/portal/invoice/:token" element={
        <Suspense fallback={<PageLoader />}>
          <PublicInvoiceView />
        </Suspense>
      } />
      {/* Sprint 5.bis.0.5 — invitación pública a asesoría. Acceso anónimo,
          el componente bifurca por authenticated state internamente y o
          bien acepta directamente (POST /accept) o redirige a /register
          con ?invitation_token=:token (handler atomic Flow A→B en BE). */}
      <Route path="/invitacion/:token" element={
        <Suspense fallback={<PageLoader />}>
          <Invitacion />
        </Suspense>
      } />
      {/* Modo Asesor v2 — aceptación de invitación firm→empresa (el dueño de la
          empresa-cliente la acepta; la página resuelve auth internamente). */}
      <Route path="/invitacion-empresa/:token" element={
        <Suspense fallback={<PageLoader />}>
          <InvitacionEmpresa />
        </Suspense>
      } />
      {/* PR-AD-0 — Contabilidad · Modo Asesor. Shell PROPIO (fuera de <Layout>):
          el workspace de asesor reemplaza el chrome global. Autenticado pero
          sin la pillar bar/topbar de Layout. Detrás de `ADVISOR_MODE`
          (+ `?advisor=1` en DEV) y del guard de asesor — ambos resueltos dentro
          de <AdvisorWorkspace>. Path B: la ruta hace switchCompany(:companyId).
          Debe ir ANTES del catch-all `/*`. */}
      <Route path="/asesor/:companyId" element={
        <AuthGuard>
          <Suspense fallback={<PageLoader />}>
            <AdvisorWorkspace />
          </Suspense>
        </AuthGuard>
      } />
      {/* Flujo de bienvenida. Shell PROPIO por el mismo motivo que el de asesor:
          un flujo de "una pregunta por pantalla" rodeado de la barra lateral y
          la topbar deja de ser un flujo y vuelve a ser un formulario más dentro
          del ERP. `OnboardingGate` redirige aquí mientras falte la identidad
          fiscal. Debe ir ANTES del catch-all `/*`. */}
      <Route path="/bienvenida" element={
        <AuthGuard>
          <Suspense fallback={<PageLoader />}>
            <Bienvenida />
          </Suspense>
        </AuthGuard>
      } />
      {import.meta.env.DEV && (
        <Route path="/aria-viz-preview" element={
          <Suspense fallback={<PageLoader />}>
            <MissionControlPreview />
          </Suspense>
        } />
      )}
      {/* Rediseño "Spatial Glass" — prototipo showcase aislado (fuera de
          <Layout>, shell propio). Gated NON_PROD: visible en local + dev
          deploy, NUNCA en producción. Reversible: retirar esta ruta y el
          directorio components/spatial + styles/spatial.css. */}
      {(import.meta.env.DEV || import.meta.env.VITE_ENV !== 'production') && (
        <Route path="/ux-lab" element={
          <Suspense fallback={<PageLoader />}>
            <UxLab />
          </Suspense>
        } />
      )}
      {/* Harness visual de los modales de creación Cliente/Proveedor. NON_PROD. */}
      {(import.meta.env.DEV || import.meta.env.VITE_ENV !== 'production') && (
        <Route path="/create-modals-harness" element={
          <Suspense fallback={<PageLoader />}>
            <CreateModalsHarness />
          </Suspense>
        } />
      )}
      {/* Harness visual del rediseño de Empleados (mock, sin auth). NON_PROD. */}
      {(import.meta.env.DEV || import.meta.env.VITE_ENV !== 'production') && (
        <Route path="/empleados-harness" element={
          <Suspense fallback={<PageLoader />}>
            <EmpleadosHarness />
          </Suspense>
        } />
      )}
      {/* Harness visual del rediseño de Inventario (mock, sin auth). NON_PROD. */}
      {(import.meta.env.DEV || import.meta.env.VITE_ENV !== 'production') && (
        <Route path="/inventario-harness" element={
          <Suspense fallback={<PageLoader />}>
            <InventarioHarness />
          </Suspense>
        } />
      )}
      <Route path="/*" element={
        <AuthGuard>
          {/* `AppProvider` va AQUÍ y no dentro de <Layout>: `FirstCompanyGate`
              decide si el ERP se monta siquiera, y sin el provider por encima
              `useApp()` le devolvía el fallback (`loading: true`) y dejaba pasar
              siempre. Se sube en vez de duplicar para no pedir /auth/me dos
              veces por carga. */}
          <AppProvider>
          {/* Antes de <Layout>, no dentro: una cuenta sin ninguna empresa no
              tiene nada que pintar en el sidebar ni en la topbar — sin empresa
              activa el backend responde 403 a toda la API. El gate se queda con
              la pantalla entera hasta que exista una empresa, y a partir de ahí
              `OnboardingGate` sigue con el embudo. */}
          <FirstCompanyGate>
          <Layout currentPageName={currentPage}>
            <ErrorBoundary title="Error loading page">
            <OnboardingGate>
            <Suspense fallback={<PageLoader />}>
              {/* Las 174 rutas autenticadas viven en `src/features/<modulo>/routes.jsx`
                  (Fase 0 / PR 0.5a). Cada manifest exporta un FRAGMENTO: React Router
                  recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se
                  mudó tal cual, sin `key` ni comas.

                  El orden NO es significativo aquí: React Router ordena por
                  especificidad, no por declaración (el catch-all `*` va siempre el
                  último, lo declare quien lo declare). Se listan por módulo para que
                  se lea de un vistazo quién aporta qué.

                  `routeTable.test.jsx` congela las 174 rutas: perder una aquí no da
                  error, da un 404. */}
              <Routes>
                {platformRoutes}
                {accountingRoutes}
                {billingRoutes}
                {bankingRoutes}
                {fiscalRoutes}
                {hrRoutes}
                {operationsRoutes}
                {aiRoutes}
              </Routes>
            </Suspense>
            </OnboardingGate>
            </ErrorBoundary>
          </Layout>
          </FirstCompanyGate>
          </AppProvider>
        </AuthGuard>
      } />
    </Routes>
  );
}

export default function Pages() {
  return (
    // basename={import.meta.env.BASE_URL}: en prod la app se sirve bajo
    // verix.es/app (edge_router le quita el prefijo antes de reenviar al
    // origin) y BASE_URL es "/app/"; en dev, que no está detrás de
    // edge_router, vite.config.js fija BASE_URL a "/" vía VITE_BASE_PATH. Sin
    // basename, cada <Link to="/..."> resolvería contra la raíz del dominio y
    // en prod aterrizaría en la landing, no en la SPA.
    <Router basename={import.meta.env.BASE_URL}>
      <PagesContent />
    </Router>
  );
}
